'use client';

/**
 * Heart toggle for a character.
 *
 * `overlay` sits in the top-right corner of a CharacterCard; `pill` matches
 * the CTA buttons on the character profile page. Both live inside or next to
 * a <Link>, so the click must never bubble into navigation.
 */
import { type MouseEvent, useState } from 'react';

import { Heart } from 'lucide-react';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { trackFunnelEvent } from '~/lib/analytics/track';
import { useFavorites } from '~/lib/hooks/use-favorites';

interface FavoriteButtonProps {
  characterId: string;
  variant?: 'overlay' | 'pill';
  className?: string;
}

export function FavoriteButton({
  characterId,
  variant = 'overlay',
  className = '',
}: FavoriteButtonProps) {
  const { signedIn, isFavorite, toggle } = useFavorites();
  const { openAuthModal } = useAuthModal();
  const [busy, setBusy] = useState(false);
  const on = isFavorite(characterId);

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;

    if (!signedIn) {
      openAuthModal('sign-up');
      return;
    }

    setBusy(true);
    try {
      const result = await toggle(characterId);
      if (!('error' in result)) {
        trackFunnelEvent(
          result.favorited ? 'character_favorited' : 'character_unfavorited',
          { characterId },
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const label = on ? 'Remove from favourites' : 'Add to favourites';

  if (variant === 'pill') {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={on}
        aria-label={label}
        disabled={busy}
        className={`inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60 ${
          on
            ? 'border-eros-pink/60 bg-eros-pink/10 text-eros-pink hover:bg-eros-pink/20'
            : 'border-white/15 bg-white/[0.03] text-white hover:bg-white/10'
        } ${className}`}
      >
        <Heart
          className="h-4 w-4"
          fill={on ? 'currentColor' : 'none'}
          strokeWidth={2.2}
        />
        {on ? 'Favourited' : 'Favourite'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={on}
      aria-label={label}
      title={label}
      disabled={busy}
      className={`flex h-8 w-8 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm transition-all hover:scale-110 hover:bg-black/65 disabled:opacity-60 ${
        on ? 'text-eros-pink' : 'text-white/90'
      } ${className}`}
    >
      <Heart
        className="h-4 w-4"
        fill={on ? 'currentColor' : 'none'}
        strokeWidth={2.2}
      />
    </button>
  );
}
